import { useState } from "react"
import { ChevronUpIcon, ChevronDownIcon, TrashIcon, CheckIcon, UploadIcon, XIcon, PlusIcon } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"

export interface QuestionOption {
  id: string
  text: string
  isCorrect: boolean
  explanation?: string
}

export type QuestionType = "multiple-choice" | "true-false" | "fill-in-the-blank"

export interface Question {
  id: string
  text: string
  type: QuestionType
  options: QuestionOption[]
  correctAnswers?: string[]
  imageUrl?: string
  imageSignedId?: string
  keepImage?: boolean
}

interface QuestionEditorProps {
  question: Question
  questionNumber: number
  uploadImageUrl?: string
  onChange?: (question: Question) => void
  onDelete?: () => void
  onMoveUp?: () => void
  onMoveDown?: () => void
  isFirst?: boolean
  isLast?: boolean
  className?: string
}

export function QuestionEditor({
  question,
  questionNumber,
  uploadImageUrl,
  onChange,
  onDelete,
  onMoveUp,
  onMoveDown,
  isFirst = false,
  isLast = false,
  className = "",
}: QuestionEditorProps) {
  const [isUploading, setIsUploading] = useState(false)

  const updateQuestion = (updates: Partial<Question>) => {
    const updated = { ...question, ...updates }
    onChange?.(updated)
  }

  const updateOption = (optionId: string, updates: Partial<QuestionOption>) => {
    if (!question.options || !Array.isArray(question.options)) return
    const updatedOptions = question.options.map((opt) =>
      opt && opt.id === optionId ? { ...opt, ...updates } : opt
    )
    updateQuestion({ options: updatedOptions })
  }

  const toggleCorrectAnswer = (optionId: string) => {
    if (!question.options || !Array.isArray(question.options)) return
    const updatedOptions = question.options.map((opt) =>
      opt ? { ...opt, isCorrect: opt.id === optionId } : opt
    )
    updateQuestion({ options: updatedOptions })
  }

  const handleTypeChange = (newType: QuestionType) => {
    const timestamp = Date.now()
    const randomSuffix = Math.random().toString(36).substring(2, 9)

    if (newType === "true-false") {
      updateQuestion({
        type: newType,
        options: [
          { id: `opt-${timestamp}-${randomSuffix}-t`, text: "True", isCorrect: true, explanation: "" },
          { id: `opt-${timestamp}-${randomSuffix}-f`, text: "False", isCorrect: false, explanation: "" },
        ],
        correctAnswers: [],
      })
    } else if (newType === "fill-in-the-blank") {
      updateQuestion({
        type: newType,
        options: [],
        correctAnswers: question.correctAnswers?.length ? question.correctAnswers : [""],
      })
    } else {
      updateQuestion({
        type: newType,
        options: [
          { id: `opt-${timestamp}-${randomSuffix}-a`, text: "", isCorrect: false, explanation: "" },
          { id: `opt-${timestamp}-${randomSuffix}-b`, text: "", isCorrect: false, explanation: "" },
          { id: `opt-${timestamp}-${randomSuffix}-c`, text: "", isCorrect: false, explanation: "" },
          { id: `opt-${timestamp}-${randomSuffix}-d`, text: "", isCorrect: false, explanation: "" },
        ],
        correctAnswers: [],
      })
    }
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !uploadImageUrl) return

    setIsUploading(true)
    try {
      const formData = new FormData()
      formData.append("image", file)
      const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
      const resp = await fetch(uploadImageUrl, {
        method: "POST",
        headers: csrfToken ? { "X-CSRF-Token": csrfToken } : {},
        body: formData,
      })
      if (!resp.ok) throw new Error("Upload failed")
      const data = await resp.json()
      updateQuestion({ imageSignedId: data.signed_id, imageUrl: data.url, keepImage: true })
    } catch (err) {
      console.error("Image upload failed:", err)
      alert("Failed to upload image. Please try again.")
    } finally {
      setIsUploading(false)
    }
  }

  const handleRemoveImage = () => {
    updateQuestion({ imageSignedId: undefined, imageUrl: undefined, keepImage: false })
  }

  const updateCorrectAnswer = (index: number, value: string) => {
    const answers = [...(question.correctAnswers || [""])]
    answers[index] = value
    updateQuestion({ correctAnswers: answers })
  }

  const addAlternativeAnswer = () => {
    const answers = [...(question.correctAnswers || [""]), ""]
    updateQuestion({ correctAnswers: answers })
  }

  const removeAlternativeAnswer = (index: number) => {
    const answers = (question.correctAnswers || [""]).filter((_, i) => i !== index)
    updateQuestion({ correctAnswers: answers.length > 0 ? answers : [""] })
  }

  const optionLabels = question.type === "true-false" ? ["T", "F"] : ["A", "B", "C", "D"]
  const typeLabel = question.type === "multiple-choice" ? "Multiple Choice"
    : question.type === "true-false" ? "True / False"
    : "Fill in the Blank"

  return (
    <div className={`bg-card border border-border rounded-lg p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-3 flex-1">
          <div className="flex flex-col gap-0.5 mt-1">
            <Button variant="ghost" size="sm" onClick={onMoveUp} disabled={isFirst}
              className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground disabled:opacity-30">
              <ChevronUpIcon className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onMoveDown} disabled={isLast}
              className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground disabled:opacity-30">
              <ChevronDownIcon className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex-1">
            <Label className="text-sm font-medium text-foreground mb-2 block">
              Question {questionNumber}
            </Label>
            <Textarea
              value={question.text}
              onChange={(e) => updateQuestion({ text: e.target.value })}
              placeholder="Enter your question here..."
              className="min-h-[80px] resize-none"
            />
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onDelete}
          className="text-destructive hover:text-destructive hover:bg-destructive/10 ml-2">
          <TrashIcon className="h-4 w-4" />
        </Button>
      </div>

      {/* Question Type Selector */}
      <div className="mb-4">
        <Label className="text-xs text-muted-foreground mb-1 block">Question Type</Label>
        <select
          value={question.type}
          onChange={(e) => handleTypeChange(e.target.value as QuestionType)}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="multiple-choice">Multiple Choice</option>
          <option value="true-false">True / False</option>
          <option value="fill-in-the-blank">Fill in the Blank</option>
        </select>
      </div>

      {/* Image Upload */}
      <div className="mb-4">
        <Label className="text-xs text-muted-foreground mb-1 block">Question Image (optional)</Label>
        {question.imageUrl ? (
          <div className="relative inline-block">
            <img src={question.imageUrl} alt="Question" className="max-h-40 rounded-md border border-border" />
            <Button variant="ghost" size="sm" onClick={handleRemoveImage}
              className="absolute top-1 right-1 h-6 w-6 p-0 bg-black/50 hover:bg-black/70 text-white rounded-full">
              <XIcon className="h-3 w-3" />
            </Button>
          </div>
        ) : (
          <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-border rounded-md cursor-pointer hover:bg-muted transition-colors text-sm text-muted-foreground">
            <UploadIcon className="h-4 w-4" />
            {isUploading ? "Uploading..." : "Upload image"}
            <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={isUploading} />
          </label>
        )}
      </div>

      {/* Options (MC / T/F) */}
      {question.type !== "fill-in-the-blank" && (
        <div className="space-y-4">
          <Label className="text-sm font-medium text-foreground">Options</Label>
          {question.options && Array.isArray(question.options) && question.options.map((option, index) => option && (
            <div key={option.id} className="space-y-2">
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center w-8 h-10 rounded-md bg-muted text-foreground font-medium text-sm shrink-0">
                  {optionLabels[index]}
                </div>
                <div className="flex-1">
                  <Input
                    value={option.text}
                    onChange={(e) => updateOption(option.id, { text: e.target.value })}
                    placeholder={`Option ${optionLabels[index]}`}
                    disabled={question.type === "true-false"}
                  />
                </div>
                <div className="flex items-center gap-2 shrink-0 h-10">
                  <Checkbox
                    id={`correct-${option.id}`}
                    checked={option.isCorrect}
                    onCheckedChange={() => toggleCorrectAnswer(option.id)}
                    className="data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                  />
                  <Label htmlFor={`correct-${option.id}`}
                    className="text-sm text-muted-foreground cursor-pointer whitespace-nowrap">
                    Correct
                  </Label>
                </div>
              </div>
              <div className="ml-11">
                <Input
                  value={option.explanation || ""}
                  onChange={(e) => updateOption(option.id, { explanation: e.target.value })}
                  placeholder="Explanation (shown after answer)"
                  className="text-sm"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Fill in the Blank answers */}
      {question.type === "fill-in-the-blank" && (
        <div className="space-y-4">
          <Label className="text-sm font-medium text-foreground">Accepted Answers</Label>
          <p className="text-xs text-muted-foreground -mt-2">
            Add accepted answers. Grading is case-insensitive and ignores extra spaces and punctuation.
          </p>
          {(question.correctAnswers || [""]).map((answer, index) => (
            <div key={index} className="flex items-center gap-2">
              <div className="flex items-center justify-center w-8 h-10 rounded-md bg-muted text-foreground font-medium text-xs shrink-0">
                {index === 0 ? "Main" : `Alt ${index}`}
              </div>
              <Input
                value={answer}
                onChange={(e) => updateCorrectAnswer(index, e.target.value)}
                placeholder={index === 0 ? "Primary correct answer" : "Alternative answer"}
                className="flex-1"
              />
              {index > 0 && (
                <Button variant="ghost" size="sm" onClick={() => removeAlternativeAnswer(index)}
                  className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive">
                  <XIcon className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
          <Button variant="ghost" size="sm" onClick={addAlternativeAnswer}
            className="text-muted-foreground hover:text-foreground">
            <PlusIcon className="h-4 w-4 mr-1" />
            Add alternative answer
          </Button>
        </div>
      )}

      {/* Correct Answer Indicator */}
      <div className="mt-4 pt-4 border-t border-border">
        <div className="flex items-center gap-2 text-sm">
          {question.type === "fill-in-the-blank" ? (
            (question.correctAnswers || []).some(a => a.trim()) ? (
              <>
                <CheckIcon className="h-4 w-4 text-green-500" />
                <span className="text-muted-foreground">
                  Accepted: <span className="text-foreground font-medium">
                    {(question.correctAnswers || []).filter(a => a.trim()).join(", ")}
                  </span>
                </span>
              </>
            ) : (
              <span className="text-destructive text-sm">Please enter at least one correct answer</span>
            )
          ) : (
            question.options?.some((opt) => opt?.isCorrect) ? (
              <>
                <CheckIcon className="h-4 w-4 text-green-500" />
                <span className="text-muted-foreground">
                  Correct answer:{" "}
                  <span className="text-foreground font-medium">
                    {question.options
                      .map((opt, idx) => (opt?.isCorrect ? optionLabels[idx] : null))
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                </span>
              </>
            ) : (
              <span className="text-destructive text-sm">Please select a correct answer</span>
            )
          )}
        </div>
      </div>
    </div>
  )
}
